import { Box, Slider, Stack, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useRtl } from "@mui/system/RtlProvider";
import type { FC, KeyboardEvent, ReactNode } from "react";
import { FONT_SIZE, MOTION } from "../../../theme/tokens";
import { originShareFor, RESOLUTION, type SignedScale, stepValue, toPosition, valueAtPosition } from "./signedScale";

// Rail opacity (as an alpha of the positive fill color) at rest, and when the control is muted.
const RAIL_ALPHA = { REST: 0.38, MUTED: 0.2 } as const;
// The tick marking the origin: a hairline taller than the rail so "zero" reads at a glance
// even when the thumb sits on it.
const ORIGIN_TICK = { WIDTH_PX: 2, HEIGHT_PX: 10 } as const;
// A `markValues` tick — shorter and lighter than the origin's, so the landmarks read as a scale
// along the rail rather than competing with zero.
const VALUE_TICK = { WIDTH_PX: 2, HEIGHT_PX: 6 } as const;
// Value steps per Shift+Arrow / PageUp / PageDown press — MUI's own `shiftStep` multiple.
const LARGE_KEY_STEPS = 10;

type Props = {
	value: number;
	min: number;
	max: number;
	// Value granularity: every value reported through `onChange` is a multiple of it, except the
	// exact `min`, `max` and `origin`, which are reported as-is so a bound is always reachable.
	step: number;
	// The value the fill grows from. Values above it read as a positive change, values below it as
	// a negative one.
	origin: number;
	// Fill + thumb color on each side of the origin, already resolved from the theme by the caller.
	fillColor: { positive: string; negative: string };
	// The smallest fraction of the rail (0…0.5) either side of the origin may take when the range
	// spans it. Without it the rail is one linear scale, so a short side (a 5-unit cut next to a
	// 150-unit add) shrinks to a sliver that can't be grabbed; with it each side keeps its own linear
	// scale and at least this much width.
	minSideShare?: number;
	// Dims the rail and fill: the "nothing dialed in" look for a control sitting at its origin.
	muted?: boolean;
	// Values worth marking on the rail (in the caller's value units, not rail positions) — the
	// landmarks a user is aiming for. Drawn as shorter ticks than the origin's.
	markValues?: number[];
	onChange: (value: number) => void;
	"aria-label": string;
	// Screen-reader text for the current value (e.g. "$12.40 saved per patient day"). Pass it whenever
	// the two sides use different scales, since equal thumb distances then mean different amounts.
	getAriaValueText?: (value: number) => string;
	// Captions under the rail's two ends (e.g. "−$4.63" / "+$144"), already formatted. Makes an
	// asymmetric scale legible: with `minSideShare`, the two halves of the rail are different sizes.
	endLabels?: { min: ReactNode; max: ReactNode };
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * A horizontal slider for a SIGNED quantity: the fill is drawn from an origin (usually 0) to the
 * thumb, so a value on either side reads as a change in that direction rather than as a fill from
 * the left edge. A tick marks the origin whenever the range spans it, and each side takes its own
 * color (e.g. spend to the right, savings to the left). `minSideShare` keeps a short side usable by
 * giving each side its own scale. When `min === origin` it behaves like an ordinary one-sided slider.
 *
 * The fill is painted into the rail as a gradient (MUI's own track is disabled) — so it stays pixel
 * aligned with the rail at any width, needs no overlay, and keeps MUI's keyboard + a11y behaviour
 * (`aria-valuenow` reports the real value, not the rail position).
 */
export const SignedSlider: FC<Props> = ({
	value,
	min,
	max,
	step,
	origin,
	fillColor,
	minSideShare,
	muted,
	markValues,
	onChange,
	"aria-label": ariaLabel,
	getAriaValueText,
	endLabels,
	dataTestId,
	sx,
}) => {
	const isRtl = useRtl();
	const spansOrigin = min < origin && origin < max;
	const scale: SignedScale = { min, max, step, origin, originShare: originShareFor(min, max, origin, minSideShare) };
	const originPosition = Math.round(scale.originShare * RESOLUTION);

	// One keyboard step = one VALUE step on either side (`stepValue`). MUI's own handler steps one rail
	// POSITION, and on a short side (an RN cut of −$4.63 over 30% of the rail is $0.0015 a position)
	// snapping that back onto the value grid lands on the same value, so the arrows did nothing.
	// Same key map as MUI's slider (Home/End stay with MUI: they jump to exact bounds already).
	const keyDirection = (key: string): number => {
		switch (key) {
			case "ArrowUp":
			case "PageUp":
				return 1;
			case "ArrowDown":
			case "PageDown":
				return -1;
			case "ArrowRight":
				return isRtl ? -1 : 1;
			case "ArrowLeft":
				return isRtl ? 1 : -1;
			default:
				return 0;
		}
	};
	// Capture phase on the root, so this runs before (and instead of) MUI's handler on the thumb input.
	const handleKeyDownCapture = (event: KeyboardEvent<HTMLSpanElement>): void => {
		const direction = keyDirection(event.key);
		if (direction === 0) return;
		event.preventDefault();
		event.stopPropagation();
		const isLarge = event.shiftKey || event.key === "PageUp" || event.key === "PageDown";
		const next = stepValue(scale, value, direction * (isLarge ? LARGE_KEY_STEPS : 1));
		if (next !== value) onChange(next);
	};

	const valuePosition = Math.round(toPosition(scale, value) * RESOLUTION);
	// MUI reads `aria-valuenow` / `aria-valuetext` through `scale` at the thumb's rail position. That
	// position is rounded, so mapping it back would snap an off-grid value (a suggested 41.0437) onto the
	// step grid and announce "$41.05" beside a visible "$41.04" — the thumb's own position reports `value`.
	const scaleAt = (position: number): number => (position === valuePosition ? value : valueAtPosition(scale, position));
	const valuePct = toPosition(scale, value) * 100;
	const originPct = scale.originShare * 100;
	const isNegative = value < origin;
	const activeColor = isNegative ? fillColor.negative : fillColor.positive;
	const fillStart = Math.min(originPct, valuePct);
	const fillEnd = Math.max(originPct, valuePct);
	const railColor = alpha(fillColor.positive, muted ? RAIL_ALPHA.MUTED : RAIL_ALPHA.REST);
	const fill = muted ? alpha(activeColor, RAIL_ALPHA.REST) : activeColor;

	// The origin tick is emitted FIRST so it is always `data-index="0"` — the only handle MUI gives
	// for styling one mark differently from the rest.
	const marks = [
		...(spansOrigin ? [{ value: originPosition }] : []),
		...(markValues ?? []).map((markValue) => ({ value: Math.round(toPosition(scale, markValue) * RESOLUTION) })),
	];

	const slider = (
		<Slider
			size="small"
			value={valuePosition}
			min={0}
			max={RESOLUTION}
			step={1}
			scale={scaleAt}
			track={false}
			marks={marks.length > 0 ? marks : false}
			onChange={(_, next) => onChange(valueAtPosition(scale, Array.isArray(next) ? next[0] : next))}
			onKeyDownCapture={handleKeyDownCapture}
			aria-label={ariaLabel}
			getAriaValueText={getAriaValueText}
			data-testid={dataTestId}
			data-direction={isNegative ? "negative" : value > origin ? "positive" : "origin"}
			sx={[
				{
					color: activeColor,
					"& .MuiSlider-rail": {
						opacity: 1,
						background: `linear-gradient(to ${isRtl ? "left" : "right"}, ${railColor} ${fillStart}%, ${fill} ${fillStart}%, ${fill} ${fillEnd}%, ${railColor} ${fillEnd}%)`,
					},
					"& .MuiSlider-mark": {
						width: VALUE_TICK.WIDTH_PX,
						height: VALUE_TICK.HEIGHT_PX,
						borderRadius: 1,
						bgcolor: "text.disabled",
						opacity: 1,
					},
					...(spansOrigin ? { '& .MuiSlider-mark[data-index="0"]': { width: ORIGIN_TICK.WIDTH_PX, height: ORIGIN_TICK.HEIGHT_PX } } : {}),
					"& .MuiSlider-thumb": {
						transition: `box-shadow ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}, background-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
						"@media (prefers-reduced-motion: reduce)": { transition: "none" },
					},
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		/>
	);
	if (!endLabels) return slider;
	return (
		<Box>
			{slider}
			<Stack
				direction="row"
				aria-hidden
				// Follow the SLIDER's direction, not the page's: an RTL page with an LTR theme still draws
				// the rail min-on-the-left, and a plain flex row would put the min caption on the right.
				sx={{
					direction: isRtl ? "rtl" : "ltr",
					justifyContent: "space-between",
					mt: -0.5,
					fontSize: FONT_SIZE.LABEL_XS,
					color: "text.secondary",
					fontVariantNumeric: "tabular-nums",
				}}
				data-testid={dataTestId ? `${dataTestId}-end-labels` : undefined}
			>
				{/* Each caption is a signed figure ("−$4.63"): isolated LTR so an RTL page keeps the sign on the
				    left of its number instead of letting the bidi algorithm move it to the far end. */}
				<Box component="span" dir="ltr" sx={{ unicodeBidi: "isolate" }}>
					{endLabels.min}
				</Box>
				<Box component="span" dir="ltr" sx={{ unicodeBidi: "isolate" }}>
					{endLabels.max}
				</Box>
			</Stack>
		</Box>
	);
};
